/**
 * Publish utility - Orchestrates the publish flow for the new editor
 * Converts client-side composition to backend meeting with sections
 */

import MeetingService from '../../../services/MeetingService';
import SectionService from '../../../services/SectionService';
import { store } from '../store';
import { selectAllUploadStates } from '../store/slices/uploadProgressSlice';
import { MEETING_STATUSES } from '../../../constants/main';

/**
 * Converts a File object back from a blob URL
 * @param {string} blobUrl - The blob URL
 * @param {string} filename - Original filename
 * @returns {Promise<File>} - The File object
 */
export async function blobUrlToFile(blobUrl, filename) {
  const response = await fetch(blobUrl);
  const blob = await response.blob();
  return new File([blob], filename, { type: blob.type });
}

/**
 * Creates a section from a video asset
 * @param {Object} params - Parameters
 * @param {string} params.meetingId - Meeting ID
 * @param {Object} params.asset - Video asset
 * @param {Object} params.timelineItem - Timeline item with trim settings
 * @param {string} params.prevSectionId - Previous section ID for ordering
 * @param {Function} params.onUploadProgress - Upload progress callback
 * @returns {Promise<Object>} - Created section
 */
export async function createMediaSection({
  meetingId,
  asset,
  timelineItem,
  prevSectionId,
  onUploadProgress,
}) {
  // Convert blob URL to File object
  const file = await blobUrlToFile(asset.url, asset.name);

  // Check if file needs remuxing (MOV files or other non-MP4/WebM formats)
  // Backend remuxes files that are not video/mp4 or video/webm (except mobile uploads)
  const needsRemuxing = asset.type === 'video' && 
    asset.mime_type && 
    !['video/mp4', 'video/webm'].includes(asset.mime_type);

  // Upload video/audio to S3 and create section using SectionService
  // SectionService.createSection handles the upload internally and detects audio vs video from file.type
  let uploadProgress = 0;
  let uploadCompleted = false;
  
  // Progress allocation:
  // - Upload: 0-70% (or 0-80% if no remuxing needed)
  // - Section creation API: 70-90% (or 80-100% if no remuxing)
  // - Remuxing: 90-100% (only for MOV files, happens during API call)
  const uploadProgressMax = needsRemuxing ? 70 : 80;
  
  // If remuxing is needed, we'll start a timer to simulate progress during the blocking API call
  let remuxProgressTimer = null;
  let currentRemuxProgress = 90; // Start at 90% when remuxing begins
  
  try {
    const section = await SectionService.createSection({
      meetingId,
      file,
      title: asset.name || `Section ${timelineItem.order + 1}`,
      prevSectionId,
      setUploadProgress: (progress) => {
        uploadProgress = progress;
        if (onUploadProgress) {
          const mappedProgress = Math.round(uploadProgress * (uploadProgressMax / 100));
          onUploadProgress(mappedProgress);
          
          // When upload completes (reaches 100%), if remuxing is needed, start simulating progress
          if (needsRemuxing && uploadProgress >= 100 && !uploadCompleted) {
            uploadCompleted = true;
            // Show 90% to indicate remuxing has started
            if (onUploadProgress) {
              onUploadProgress(90);
            }
            
            // Start simulating remuxing progress: increment 1% every 5 seconds
            // Cap at 99% to leave room for final completion
            if (!remuxProgressTimer) {
              remuxProgressTimer = setInterval(() => {
                if (currentRemuxProgress < 99) {
                  currentRemuxProgress += 1;
                  if (onUploadProgress) {
                    onUploadProgress(currentRemuxProgress);
                  }
                }
              }, 5000); // 5 seconds = 5000ms
            }
          }
        }
      },
    });

    // Clear the remuxing progress timer when API call completes
    if (remuxProgressTimer) {
      clearInterval(remuxProgressTimer);
      remuxProgressTimer = null;
    }

    // If remuxing was needed, update to 100% to show completion
    if (needsRemuxing && onUploadProgress) {
      onUploadProgress(100);
    }

    // Trim settings are stored as metadata only - no server-side trimming needed
    // Trimming will be applied client-side during playback and server-side only on download/export
    // Trim settings are passed to backend via section metadata for later use

    return section;
  } catch (error) {
    // Clear the timer on error
    if (remuxProgressTimer) {
      clearInterval(remuxProgressTimer);
      remuxProgressTimer = null;
    }
    throw error;
  }
}

// Legacy alias for backwards compatibility
const createVideoSection = createMediaSection;

/**
 * Creates attachment sections from image/document assets
 * @param {Object} params - Parameters
 * @param {string} params.meetingId - Meeting ID
 * @param {Array} params.assets - Image/document assets
 * @param {Array} params.timelineItems - Timeline items
 * @param {string} params.prevSectionId - Previous section ID for ordering
 * @param {Function} params.onUploadProgress - Upload progress callback
 * @returns {Promise<Object>} - Created section
 */
export async function createAttachmentSections({
  meetingId,
  assets,
  timelineItems,
  prevSectionId,
  onUploadProgress,
}) {
  // Convert blob URLs to File objects
  const files = await Promise.all(
    assets.map(async (asset) => {
      const file = await blobUrlToFile(asset.url, asset.name);
      return file;
    })
  );

  // Extract descriptions from asset metadata
  // Match descriptions to files by name (since we stored them in metadata)
  const descriptions = assets.map((asset, index) => {
    const file = files[index];
    return {
      name: asset.metadata?.attachmentName || asset.name || file.name,
      description: asset.metadata?.attachmentDescription || '',
      size: file.size,
    };
  });

  // Create attachment sections using SectionService
  // Use the first asset's name as the section title to preserve the original file name
  const sectionTitle = assets[0]?.name || `Attachments ${timelineItems[0]?.order + 1 || 1}`;
  const section = await SectionService.createAttachmentSections({
    meetingId,
    files,
    title: sectionTitle,
    prevSectionId,
    type: assets[0].type, // 'image' or 'document'
    descriptions,
    setUploadProgress: (progress) => {
      if (onUploadProgress) {
        onUploadProgress(progress);
      }
    },
  });

  return section;
}

/**
 * Publishes a composition to the backend
 * @param {Object} params - Publish parameters
 * @param {string} params.title - Meeting title
 * @param {string} params.folderId - Folder ID (optional)
 * @param {string} params.comment - Publish comment (optional)
 * @param {Array} params.assets - All assets (videos, images, documents)
 * @param {Array} params.timelineItems - Timeline items in order
 * @param {string} params.meetingId - Existing meeting ID (for edit mode)
 * @param {Function} params.onProgress - Progress callback (sectionIndex, totalSections, progress)
 * @returns {Promise<Object>} - Published meeting
 */
export async function publishComposition({
  title,
  folderId,
  comment,
  assets,
  timelineItems,
  meetingId: existingMeetingId,
  fetchedSections,
  pendingSaveItemIds,
  onProgress,
}) {
  // Filter to video, image, and document assets
  const videoItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'video';
  });

  const imageItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'image';
  });

  const documentItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'document';
  });

  const questionItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'question';
  });

  const textItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'text';
  });

  const audioItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'audio';
  });

  const assignedSectionItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'assigned-section';
  });

  // Check for remaining placeholder sections - these must be replaced before publishing
  const placeholderItems = timelineItems.filter((item) => {
    const asset = assets.find((a) => a.id === item.asset_id);
    return asset && asset.type === 'placeholder';
  });

  if (placeholderItems.length > 0) {
    throw new Error(
      `Cannot publish with ${placeholderItems.length} placeholder section${placeholderItems.length > 1 ? 's' : ''} remaining. ` +
      'Please add content to all placeholder sections or remove them before publishing.'
    );
  }

  if (
    videoItems.length === 0 &&
    imageItems.length === 0 &&
    documentItems.length === 0 &&
    questionItems.length === 0 &&
    textItems.length === 0 &&
    audioItems.length === 0 &&
    assignedSectionItems.length === 0
  ) {
    throw new Error('At least one asset is required to publish');
  }

  // Validate that no uploads are currently in progress
  const uploadStates = selectAllUploadStates(store.getState());
  const activeUploads = Object.values(uploadStates).filter(state =>
    state.status === 'uploading' || state.status === 'queued'
  );

  if (activeUploads.length > 0) {
    throw new Error(
      `Cannot publish while uploads are in progress. ` +
      `Please wait for ${activeUploads.length} upload${activeUploads.length > 1 ? 's' : ''} to complete.`
    );
  }

  // Calculate total items for progress tracking
  const totalItems =
    videoItems.length + imageItems.length + documentItems.length + questionItems.length + textItems.length + audioItems.length + assignedSectionItems.length;

  // Step 1: Create or use existing meeting
  if (onProgress) {
    onProgress(0, totalItems, 0);
  }

  let meeting;
  if (existingMeetingId) {
    // Edit mode: use existing meeting
    meeting = await MeetingService.getMeeting(existingMeetingId);
    
    // Update meeting title if changed
    if (meeting.title !== title) {
      await MeetingService.updateMeeting({
        meetingId: existingMeetingId,
        title,
      });
      meeting.title = title;
    }
  } else {
    // Create mode: create new meeting
    meeting = await MeetingService.createMeeting({
      title,
      folderId: folderId || null,
      type: 'my videos',
    });
  }

  // Step 2: Sync section changes (mimic existing app behavior)
  // IMPORTANT: When publishing from a draft, fetchedSections might include draft sections
  // We need to exclude draft sections from the deletion logic here.
  if (existingMeetingId && fetchedSections && fetchedSections.length > 0) {
    // Get draft section IDs to exclude them from deletion
    const draftSectionIds = meeting.draftData?.sectionIds ? new Set(meeting.draftData.sectionIds) : new Set();
    
    // Get all original section IDs from fetchedSections, EXCLUDING draft sections
    // Only consider regular (non-draft) sections for deletion
    const originalSectionIds = new Set(
      fetchedSections
        .filter((s) => !draftSectionIds.has(s.id)) // Exclude draft sections
        .map((s) => s.id)
    );

    // Get section IDs that still exist in timeline (from project assets)
    // IMPORTANT: Include audioItems and assignedSectionItems to prevent them from being deleted
    const allItems = [...videoItems, ...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems, ...assignedSectionItems];
    // Reuse uploadStates from above (line 259) to avoid shadowing
    const remainingSectionIds = new Set(
      allItems
        .map((item) => {
          const asset = assets.find((a) => a.id === item.asset_id);
          // Check metadata first
          if (asset?.source_type === 'project' && item.metadata?.sectionId) {
            return item.metadata.sectionId;
          }
          // Also check Redux upload state for completed background uploads
          if (asset && uploadStates[asset.id]?.status === 'completed' && uploadStates[asset.id]?.sectionId) {
            return uploadStates[asset.id].sectionId;
          }
          return null;
        })
        .filter((id) => id !== null)
    );

    // Only delete regular sections (not draft sections) that are no longer in timeline
    const sectionsToDelete = Array.from(originalSectionIds).filter(
      (id) => !remainingSectionIds.has(id)
    );

    if (sectionsToDelete.length > 0) {
      try {
        await SectionService.deleteSectionsBatch({
          meetingId: meeting.id,
          ids: sectionsToDelete,
        });
      } catch (batchError) {
        // Fallback to sequential deletes if batch endpoint is unavailable
        // eslint-disable-next-line no-console
        console.warn('Batch section delete failed, falling back to sequential deletes', batchError);
        // eslint-disable-next-line no-restricted-syntax, no-await-in-loop
        for (const sectionId of sectionsToDelete) {
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.deleteSection({ sectionId });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to delete section during publish: ${errorMessage}`);
          }
        }
      }
    }

    // Update existing sections (titles/trim/attachment metadata) using batch endpoint with fallback
    const originalSectionMap = new Map((fetchedSections || []).map((s) => [s.id, s]));
    const sectionUpdateMap = new Map(); // sectionId -> { sectionId, title?, trimSettings?, attachment? }

    const queueUpdate = (sectionId, data) => {
      if (!sectionId) return;
      const existing = sectionUpdateMap.get(sectionId) || { sectionId };
      sectionUpdateMap.set(sectionId, { ...existing, ...data });
    };

    // Videos (titles + trim)
    videoItems.forEach((item) => {
      const asset = assets.find((a) => a.id === item.asset_id);
      const { sectionId } = item.metadata || {};
      if (!asset || asset.type !== 'video' || asset.source_type !== 'project' || !sectionId) {
        return;
      }

      const originalSection = originalSectionMap.get(sectionId);
      const originalTitle = originalSection?.title || null;
      const titleChanged = asset.name && asset.name !== originalTitle;
      if (titleChanged) {
        queueUpdate(sectionId, { title: asset.name });
      }

      const trimStart = item.settings?.trim_start ?? 0;
      const trimEnd = item.settings?.trim_end ?? 0;
      const originalDuration = asset.duration ?? (item.duration + trimStart + trimEnd);
      const originalTrim = originalSection?.trimSettings || null;
      const hasTrim = trimStart > 0 || trimEnd > 0;
      const trimChanged =
        (originalTrim?.trimStart ?? 0) !== trimStart ||
        (originalTrim?.trimEnd ?? 0) !== trimEnd ||
        (originalTrim?.originalDuration ?? originalDuration) !== originalDuration;

      if (trimChanged) {
        queueUpdate(sectionId, {
          trimSettings: hasTrim
            ? {
                trimStart,
                trimEnd,
                originalDuration,
              }
            : null,
        });
      }
    });

    // Other items (audio trim, titles, attachments)
    const otherItems = [...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems];
    otherItems.forEach((item) => {
      const asset = assets.find((a) => a.id === item.asset_id);
      const { sectionId, attachmentId } = item.metadata || {};

      if (!asset || asset.source_type !== 'project' || !sectionId) {
        return;
      }

      const originalSection = originalSectionMap.get(sectionId);
      const originalTitle = originalSection?.title || null;
      const titleChanged = asset.name && asset.name !== originalTitle;
      if (titleChanged) {
        queueUpdate(sectionId, { title: asset.name });
      }

      if (asset.type === 'audio') {
        const trimStart = item.settings?.trim_start ?? 0;
        const trimEnd = item.settings?.trim_end ?? 0;
        const originalDuration = asset.duration ?? (item.duration + trimStart + trimEnd);
        const originalTrim = originalSection?.trimSettings || null;
        const hasTrim = trimStart > 0 || trimEnd > 0;
        const trimChanged =
          (originalTrim?.trimStart ?? 0) !== trimStart ||
          (originalTrim?.trimEnd ?? 0) !== trimEnd ||
          (originalTrim?.originalDuration ?? originalDuration) !== originalDuration;

        if (trimChanged) {
          queueUpdate(sectionId, {
            trimSettings: hasTrim
              ? {
                  trimStart,
                  trimEnd,
                  originalDuration,
                }
              : null,
          });
        }
      }

      if ((asset.type === 'image' || asset.type === 'document') && attachmentId) {
        const originalAttachment = originalSection?.SectionAttachments?.find((att) => att.id === attachmentId);
        const originalAttachmentName = originalAttachment?.fileName || originalAttachment?.name || '';
        const originalAttachmentDescription = originalAttachment?.description || '';
        const newAttachmentName = asset.metadata?.attachmentName || asset.name || '';
        const newAttachmentDescription = asset.metadata?.attachmentDescription || '';

        const attachmentChanged =
          newAttachmentName !== originalAttachmentName || newAttachmentDescription !== originalAttachmentDescription;

        if (attachmentChanged) {
          queueUpdate(sectionId, {
            attachment: {
              id: attachmentId,
              name: newAttachmentName,
              description: newAttachmentDescription,
            },
          });
        }
      }
    });

    const sectionUpdates = Array.from(sectionUpdateMap.values());

    if (sectionUpdates.length > 0) {
      try {
        await SectionService.updateSectionsBatch({
          meetingId: meeting.id,
          items: sectionUpdates,
        });
      } catch (batchError) {
        // Fallback to sequential updates if batch endpoint is unavailable or fails
        // eslint-disable-next-line no-console
        console.warn('Batch section update failed, falling back to sequential updates', batchError);

        // eslint-disable-next-line no-restricted-syntax
        for (const update of sectionUpdates) {
          const { sectionId, title: updateTitle, attachment, trimSettings } = update;

          if (updateTitle) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.updateSection({
                sectionId,
                title: updateTitle,
              });
            } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to update section title "${updateTitle}" during publish: ${errorMessage}`);
            }
          }

          if (trimSettings !== undefined) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.updateSection({
                sectionId,
                trimSettings,
              });
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to update trim settings during publish: ${errorMessage}`);
            }
          }

          if (attachment) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.updateSectionAttachment({
                sectionId,
                attachment,
              });
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to update attachment metadata during publish: ${errorMessage}`);
            }
          }
          if (update.trimSettings !== undefined) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.updateSection({
                sectionId,
                trimSettings: update.trimSettings,
              });
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to update trim settings during publish: ${errorMessage}`);
            }
          }
        }
      }
    }

    // Update section orders to match timeline order
    // ARCHITECTURAL PRINCIPLE: During publish, we set Section.order from timeline order
    // This is when Section.order becomes the source of truth (for published meetings)
    // Sort all items by timeline order
    const allItemsForOrder = [...videoItems, ...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems, ...assignedSectionItems].sort(
      (a, b) => a.order - b.order
    );
    const orderPayload = allItemsForOrder
      .map((item, index) => {
        const asset = assets.find((a) => a.id === item.asset_id);
        const { sectionId } = item.metadata || {};
        if (asset && asset.source_type === 'project' && sectionId) {
          return { sectionId, order: index };
        }
        return null;
      })
      .filter(Boolean);

    if (orderPayload.length > 0) {
      try {
        await SectionService.updateSectionOrders({
          meetingId: meeting.id,
          orders: orderPayload,
        });
      } catch (batchError) {
        // Fallback to sequential updates if batch endpoint is unavailable
        // eslint-disable-next-line no-console
        console.warn('Batch section order update failed, falling back to sequential updates', batchError);
        // eslint-disable-next-line no-restricted-syntax
        for (let i = 0; i < orderPayload.length; i += 1) {
          const { sectionId, order } = orderPayload[i];
          try {
            // eslint-disable-next-line no-await-in-loop
            await SectionService.updateSection({
              sectionId,
              order,
            });
          } catch (error) {
            const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
            throw new Error(`Failed to update section order during publish: ${errorMessage}`);
          }
        }
      }
    }

    // Note: Images and documents don't support trimming, so we skip them in edit mode
    // They are handled as existing sections if they exist, or created as new if added
  }

  // Step 3: Create new sections for newly uploaded assets
  // ARCHITECTURAL PRINCIPLE: During publish, we set Section.order from timeline order
  // New sections are created with prevSectionId from timeline order
  // Backend handles positioning via draftData.sectionIds (if draft) or repositionSections (if published)
  // Track ALL section IDs in timeline order (used for final order update)
  const createdSectionIds = []; // Track section IDs in timeline order

  if (existingMeetingId) {
    // Edit mode: Create new sections, maintaining order with existing ones
    // Process sections sequentially to create new ones and maintain order
    let currentIndex = 0;
    // Sort all items by their order in the timeline to maintain correct sequence
    const allItems = [...videoItems, ...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems, ...assignedSectionItems].sort(
      (a, b) => a.order - b.order
    );

    // Match sections from fetchedSections to timeline items if publishing from draft
    // This prevents duplicate section creation when publishing immediately after saveDraft
    const sectionIdMap = new Map(); // Maps asset.id -> sectionId from fetchedSections
    if (fetchedSections && fetchedSections.length > 0 && meeting.draftData?.sectionIds) {
      // Sections in fetchedSections are in the same order as draftData.sectionIds
      // Match them to timeline items by order/index
      const draftSectionIds = meeting.draftData.sectionIds;
      allItems.forEach((item, index) => {
        if (index < draftSectionIds.length && index < fetchedSections.length) {
          const sectionId = draftSectionIds[index];
          const section = fetchedSections.find((s) => s.id === sectionId);
          if (section) {
            sectionIdMap.set(item.asset_id, sectionId);
          }
        }
      });
    }

    // Calculate existing and new counts once
    const existingCount = allItems.filter((it) => {
      const a = assets.find((x) => x.id === it.asset_id);
      // Check if section exists in fetchedSections (from saveDraft) or if asset is marked as project
      return a?.source_type === 'project' || sectionIdMap.has(it.asset_id);
    }).length;
    const newCount = allItems.length - existingCount;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < allItems.length; i += 1) {
      const item = allItems[i];
      const asset = assets.find((a) => a.id === item.asset_id);

        if (asset) {
        // Handle assigned-section sections
        if (asset.type === 'assigned-section') {
          // If already created (from previous save), reuse it
        if (asset.source_type === 'project' && item.metadata?.sectionId) {
            createdSectionIds.push(item.metadata.sectionId);
          // Skip creating assigned-section sections - they're already in the backend
            // eslint-disable-next-line no-continue
            continue;
          }
          
          // Check if section exists in fetchedSections (from saveDraft)
          const existingSectionId = sectionIdMap.get(item.asset_id);
          if (existingSectionId) {
            createdSectionIds.push(existingSectionId);
            // eslint-disable-next-line no-continue
            continue;
          }

          // New assigned section (source_type: 'upload') - create it now
          if (asset.source_type === 'upload') {
            const assignedEmail = asset.metadata?.assignedEmail || item.metadata?.assignedEmail;
            const message = asset.metadata?.message || item.metadata?.message || '';

            if (!assignedEmail) {
              // eslint-disable-next-line no-console
              console.warn('Assigned section missing email, skipping');
              // eslint-disable-next-line no-continue
              continue;
            }

            // Determine prevSectionId from timeline order (last section in createdSectionIds)
            const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

            // Call assign-section API to create the section
            // Backend handles positioning via draftData or repositionSections
            // eslint-disable-next-line no-await-in-loop
            const response = await MeetingService.assignSection({
              meetingId: meeting.id,
              email: assignedEmail,
              message,
              prevSectionId,
            });

            if (response.section) {
              const { section } = response;
              createdSectionIds.push(section.id);
              currentIndex += 1;
            } else {
              // eslint-disable-next-line no-console
              console.warn('Failed to create assigned section, response missing section object');
            }
            // eslint-disable-next-line no-continue
            continue;
          }
        }

        // Check if section already exists (from fetchedSections or from asset metadata)
        const existingSectionId = sectionIdMap.get(item.asset_id) || (asset.source_type === 'project' && item.metadata?.sectionId ? item.metadata.sectionId : null);
        
        if (existingSectionId) {
          // Existing section - track in timeline order
          createdSectionIds.push(existingSectionId);
        } else if (asset.source_type === 'project' && asset.metadata?.projectMeetingId) {
          // Library-picked video: use server-side copy
          const currentItemIndex = i;
          const currentUploadIndex = currentIndex;
          
          // Update progress: server-side copy is fast, show progress immediately
          if (onProgress) {
            const currentNewIndex = allItems.slice(0, currentItemIndex + 1).filter((it) => {
              const a = assets.find((x) => x.id === it.asset_id);
              return a?.source_type === 'upload' || (a?.source_type === 'project' && a.metadata?.projectMeetingId);
            }).length - 1;
            
            const sectionStart = (existingCount / allItems.length) * 80;
            const sectionProgressPercent = (currentNewIndex / newCount) * 80;
            onProgress(currentUploadIndex, allItems.length, Math.round(sectionStart + sectionProgressPercent));
          }

          // Determine prevSectionId from timeline order (last section in createdSectionIds)
          const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

          // Get source section ID from the library meeting
          // Saved sections are stored as meetings with typically one section
          // eslint-disable-next-line no-await-in-loop
          const sourceSections = await SectionService.getSections(asset.metadata.projectMeetingId);
          if (!sourceSections || sourceSections.length === 0) {
            // eslint-disable-next-line no-console
            console.error(`No sections found in library meeting ${asset.metadata.projectMeetingId}`);
            // eslint-disable-next-line no-continue
            continue;
          }
          const sourceSectionId = sourceSections[0].id;

          // Get trim settings from timeline item
          const trimStart = item.settings?.trim_start ?? 0;
          const trimEnd = item.settings?.trim_end ?? 0;

          // Use server-side copy instead of client-side download/re-upload
          // Backend will handle S3 copy (untrimmed) or MediaConvert (trimmed)
          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.copySectionFromLibrary({
            sourceSectionId,
            meetingId: meeting.id,
            title: asset.name,
            trimStart,
            trimEnd,
            prevSectionId,
          });

          createdSectionIds.push(section.id);
          currentIndex += 1;

          // Update progress after section is created
          if (onProgress) {
            const currentNewIndex = allItems.slice(0, currentItemIndex + 1).filter((it) => {
              const a = assets.find((x) => x.id === it.asset_id);
              return a?.source_type === 'upload' || (a?.source_type === 'project' && a.metadata?.projectMeetingId);
            }).length;

            const sectionStart = (existingCount / allItems.length) * 80;
            const sectionProgressPercent = (currentNewIndex / newCount) * 80;
            onProgress(currentUploadIndex, allItems.length, Math.round(sectionStart + sectionProgressPercent));
          }

          // Save section to library if user marked it (e.g., mobile uploads imported from library)
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
        } else if (asset.source_type === 'upload') {
          // Check if upload is already completed via background upload
          // Reuse uploadStates from above (line 259) to avoid shadowing
          const uploadState = uploadStates[asset.id];
          if (uploadState && uploadState.status === 'completed') {
            // Upload already completed! Just link the existing section
            const { sectionId } = uploadState;
            createdSectionIds.push(sectionId);

            // Save section to library if user marked it
            if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.saveSection(sectionId);
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
              }
            }

            // Update progress
            if (onProgress) {
              const currentNewIndex = allItems.slice(0, i + 1).filter((it) => {
                const a = assets.find((x) => x.id === it.asset_id);
                return a?.source_type === 'upload';
              }).length;

              const sectionStart = (existingCount / allItems.length) * 80;
              const sectionProgressPercent = (currentNewIndex / newCount) * 80;
              onProgress(i, allItems.length, Math.round(sectionStart + sectionProgressPercent));
            }

            currentIndex += 1;
            // eslint-disable-next-line no-continue
            continue; // Skip to next item
          }

          // New section - create it
          const currentItemIndex = i;
          const currentUploadIndex = currentIndex;
          const sectionProgress = (progress) => {
            // Progress calculation: existing sections + new sections
            const currentNewIndex = allItems.slice(0, currentItemIndex + 1).filter((it) => {
              const a = assets.find((x) => x.id === it.asset_id);
              return a?.source_type === 'upload';
            }).length - 1;
            
            const sectionStart = (existingCount / allItems.length) * 80;
            const sectionProgressPercent = ((currentNewIndex + progress / 100) / newCount) * 80;
            if (onProgress) {
              onProgress(currentUploadIndex, allItems.length, Math.round(sectionStart + sectionProgressPercent));
            }
          };

          // Determine prevSectionId from timeline order (last section in createdSectionIds)
          const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

          if (asset.type === 'video' || asset.type === 'audio') {
            // eslint-disable-next-line no-await-in-loop
            const section = await createVideoSection({
              meetingId: meeting.id,
              asset,
              timelineItem: item,
              prevSectionId,
              onUploadProgress: sectionProgress,
            });

            createdSectionIds.push(section.id);
            // If user marked this item to be saved after publish, do it now
            if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.saveSection(section.id);
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
              }
            }
            
          } else if (asset.type === 'image' || asset.type === 'document') {
            // Create individual attachment section for each image/document
            // eslint-disable-next-line no-await-in-loop
            const section = await createAttachmentSections({
              meetingId: meeting.id,
              assets: [asset],
              timelineItems: [item],
              prevSectionId,
              onUploadProgress: sectionProgress,
            });

            createdSectionIds.push(section.id);
            if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.saveSection(section.id);
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
              }
            }
          } else if (asset.type === 'question') {
            // Create question section using existing endpoint contract
            // eslint-disable-next-line no-await-in-loop
            const section = await SectionService.createSection({
              meetingId: meeting.id,
              title: asset.name,
              prevSectionId,
              question: {
                isAnswerRequired: !!asset.isAnswerRequired,
                text: asset.text,
                options: asset.options || null,
              },
            });

            createdSectionIds.push(section.id);
            if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.saveSection(section.id);
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
              }
            }
          } else if (asset.type === 'text') {
            // Create text (note) section
            // eslint-disable-next-line no-await-in-loop
            const section = await SectionService.createSection({
              meetingId: meeting.id,
              title: asset.name || 'Text',
              prevSectionId,
              text: asset.text || '',
            });
            createdSectionIds.push(section.id);
            if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
              try {
                // eslint-disable-next-line no-await-in-loop
                await SectionService.saveSection(section.id);
              } catch (error) {
                const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
                throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
              }
            }
          }

          currentIndex += 1;
        }
      }
    }
  } else {
    // Create mode: create all sections in timeline order
    // ARCHITECTURAL PRINCIPLE: Use timeline order for prevSectionId chain
    // Sort all items by their order in the timeline
    const allItems = [...videoItems, ...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems, ...assignedSectionItems].sort(
      (a, b) => a.order - b.order
    );

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < allItems.length; i += 1) {
      const item = allItems[i];
      const asset = assets.find((a) => a.id === item.asset_id);

      if (asset) {
        // Assigned-section sections are already created when assigned, just preserve them
        if (asset.type === 'assigned-section' && asset.source_type === 'project' && item.metadata?.sectionId) {
          createdSectionIds.push(item.metadata.sectionId);
          // eslint-disable-next-line no-continue
          continue;
        }

        if (asset.source_type === 'project' && asset.metadata?.projectMeetingId) {
          // Library-picked video: use server-side copy
          // Update progress: server-side copy is fast, show progress immediately
          if (onProgress) {
            const sectionStart = (i / totalItems) * 80;
            onProgress(i, totalItems, Math.round(sectionStart));
          }

          // Determine prevSectionId from timeline order (last section in createdSectionIds)
          const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

          // Get source section ID from the library meeting
          // Saved sections are stored as meetings with typically one section
          // eslint-disable-next-line no-await-in-loop
          const sourceSections = await SectionService.getSections(asset.metadata.projectMeetingId);
          if (!sourceSections || sourceSections.length === 0) {
            // eslint-disable-next-line no-console
            console.error(`No sections found in library meeting ${asset.metadata.projectMeetingId}`);
            // eslint-disable-next-line no-continue
            continue;
          }
          const sourceSectionId = sourceSections[0].id;

          // Get trim settings from timeline item
          const trimStart = item.settings?.trim_start ?? 0;
          const trimEnd = item.settings?.trim_end ?? 0;

          // Use server-side copy instead of client-side download/re-upload
          // Backend will handle S3 copy (untrimmed) or MediaConvert (trimmed)
          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.copySectionFromLibrary({
            sourceSectionId,
            meetingId: meeting.id,
            title: asset.name,
            trimStart,
            trimEnd,
            prevSectionId,
          });

          createdSectionIds.push(section.id);
          
          // Update progress after section is created
          if (onProgress) {
            const sectionEnd = ((i + 1) / totalItems) * 80;
            onProgress(i + 1, totalItems, Math.round(sectionEnd));
          }

          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
          // eslint-disable-next-line no-continue
          continue;
        }

        const currentItemIndex = i;
        const sectionProgress = (progress) => {
          const sectionStart = (currentItemIndex / totalItems) * 80;
          const sectionProgressPercent = (progress / totalItems) * 0.8;
          if (onProgress) {
            onProgress(currentItemIndex, totalItems, Math.round(sectionStart + sectionProgressPercent));
          }
        };

        // Determine prevSectionId from timeline order (last section in createdSectionIds)
        const prevSectionId = createdSectionIds.length > 0 ? createdSectionIds[createdSectionIds.length - 1] : null;

        if (asset.type === 'video' || asset.type === 'audio') {
          // eslint-disable-next-line no-await-in-loop
          const section = await createVideoSection({
            meetingId: meeting.id,
            asset,
            timelineItem: item,
            prevSectionId,
            onUploadProgress: sectionProgress,
          });

          createdSectionIds.push(section.id);
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'image' || asset.type === 'document') {
          // Create individual attachment section for each image/document
          // eslint-disable-next-line no-await-in-loop
          const section = await createAttachmentSections({
            meetingId: meeting.id,
            assets: [asset],
            timelineItems: [item],
            prevSectionId,
            onUploadProgress: sectionProgress,
          });

          createdSectionIds.push(section.id);
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'question') {
          // Create question section using existing endpoint contract
          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.createSection({
            meetingId: meeting.id,
            title: asset.name,
            prevSectionId,
            question: {
              isAnswerRequired: !!asset.isAnswerRequired,
              text: asset.text,
              options: asset.options || null,
            },
          });

          createdSectionIds.push(section.id);
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
        } else if (asset.type === 'text') {
          // Create text (note) section
          // eslint-disable-next-line no-await-in-loop
          const section = await SectionService.createSection({
            meetingId: meeting.id,
            title: asset.name || 'Text',
            prevSectionId,
            text: asset.text || '',
          });

          createdSectionIds.push(section.id);
          if (pendingSaveItemIds && pendingSaveItemIds.has && pendingSaveItemIds.has(item.id)) {
            try {
              // eslint-disable-next-line no-await-in-loop
              await SectionService.saveSection(section.id);
            } catch (error) {
              const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
              throw new Error(`Failed to save section to library during publish: ${errorMessage}`);
            }
          }
        }
      }
    }
  }

  // Step 4: Final order update - ensure ALL sections (including assigned-sections) have correct order
  // CRITICAL: This must happen AFTER all sections are created/processed
  // The createdSectionIds array now contains all sections in timeline order
  // This ensures assigned-sections maintain their position from the timeline
  if (createdSectionIds.length > 0) {
    // Update ALL sections' order to match their position in createdSectionIds array
    // This ensures assigned-sections and all other sections maintain their timeline order
    // Works for both edit mode (existingMeetingId) and create mode
    // eslint-disable-next-line no-restricted-syntax
    for (let i = 0; i < createdSectionIds.length; i += 1) {
      const sectionId = createdSectionIds[i];
      try {
        // eslint-disable-next-line no-await-in-loop
        await SectionService.updateSection({
          sectionId,
          order: i,
        });
      } catch (error) {
        const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
        throw new Error(`Failed to update section order during publish: ${errorMessage}`);
      }
    }
  }

  // Step 5: Update draftData.sectionIds to match current timeline order BEFORE publishing
  // This ensures the backend uses the correct order when creating SectionVersion records
  // CRITICAL: Even if user didn't save, we need to sync draftData.sectionIds to timeline order
  if (createdSectionIds.length > 0) {
    // Update draftData.sectionIds to match the current timeline order
    // This ensures backend publish uses correct order even if user didn't manually save
    const updatedMeeting = await MeetingService.updateMeeting({
      meetingId: meeting.id,
      draftData: {
        version: '1.0.0',
        sectionIds: createdSectionIds,
        savedAt: new Date().toISOString(),
      },
    });
    // Refresh meeting object to ensure we have latest draftData
    meeting = updatedMeeting;
  }

  // Step 5.1: Publish meeting (mimic existing app - just creates version snapshot)
  if (onProgress) {
    onProgress(totalItems, totalItems, 95);
  }

  // Step 5.5: Clean up draft sections and draftData after publishing
  // IMPORTANT: Draft sections that are in the timeline are being published (they become regular sections)
  // We should NOT delete any draft sections that are in the timeline
  // Only delete orphaned draft sections (not in timeline)
  if (meeting.draftData?.sectionIds && meeting.draftData.sectionIds.length > 0) {
    // Get all sectionIds from current timeline items (sections being published)
    const sectionsInTimeline = new Set();
    const allTimelineItems = [...videoItems, ...audioItems, ...imageItems, ...documentItems, ...questionItems, ...textItems, ...assignedSectionItems];
    
    // Collect all sectionIds from timeline items
    allTimelineItems.forEach((item) => {
      const sectionId = item.metadata?.sectionId;
      if (sectionId) {
        sectionsInTimeline.add(sectionId);
      }
    });

    // Only delete draft sections that are NOT in the timeline (orphaned sections)
    // Sections in the timeline are being published and must be kept
    const draftSectionsToDelete = meeting.draftData.sectionIds.filter(
      (sectionId) => !sectionsInTimeline.has(sectionId)
    );

    // Safety check: If we're about to delete ALL draft sections, something is wrong
    // This means no timeline items have sectionId, which shouldn't happen when publishing from a draft
    if (draftSectionsToDelete.length === meeting.draftData.sectionIds.length && allTimelineItems.length > 0) {
      // eslint-disable-next-line no-console
      console.error('WARNING: All draft sections would be deleted, but timeline has items. This indicates a bug. Skipping deletion.');
      // Don't delete anything - this is a safety measure
    } else {
      // Only delete orphaned draft sections (not in timeline)
      // DO NOT delete draft sections that are in the timeline - they're being published!
      // eslint-disable-next-line no-restricted-syntax
      for (const sectionId of draftSectionsToDelete) {
        try {
          // eslint-disable-next-line no-await-in-loop
          await SectionService.deleteSection({ sectionId });
        } catch (error) {
          const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
          throw new Error(`Failed to delete draft section during publish: ${errorMessage}`);
        }
      }
    }
  }

  const publishedMeeting = await MeetingService.updateMeeting({
    meetingId: meeting.id,
    title,
    comment,
    shouldCreateNewVersion: true,
    status: MEETING_STATUSES.PUBLISHED,
    draftData: null, // Clear draftData on publish
  });

  if (onProgress) {
    onProgress(totalItems, totalItems, 100);
  }

  return publishedMeeting;
}
